import React, { useEffect, useContext, Dispatch, SetStateAction } from 'react'
import { format, add, sub } from 'date-fns'
import { fr } from 'date-fns/locale'
import { PERIOD } from 'core/constants/period'
import { Trans, useTranslation } from 'react-i18next'
import { ReactComponent as PrevIcon } from 'core/assets/images/icones/prevIcon.svg'
import { ReactComponent as NextIcon } from 'core/assets/images/icones/nextIcon.svg'
import { ReactComponent as PrevIconHover } from 'core/assets/images/icones/prevIconHover.svg'
import { ReactComponent as NextIconHover } from 'core/assets/images/icones/nextIconHover.svg'
import { ReactComponent as PrevIconDark } from 'core/assets/images/icones/prevIconDark.svg'
import { ReactComponent as NextIconDark } from 'core/assets/images/icones/nextIconDark.svg'
import { DarkThemeContext } from 'core/contexts/DarkThemeContext'
import { getPeriodDates } from 'core/utils/service'

export const PeriodSelector: React.FC<{
  periodState: { period: string; setPeriod: Dispatch<SetStateAction<string>> }
  dateRange: {
    currentDate: Date
    setCurrentDate: Dispatch<SetStateAction<Date>>
    start: Date
    setStart: Dispatch<SetStateAction<Date>>
    end: Date
    setEnd: Dispatch<SetStateAction<Date>>
  }
}> = ({ periodState, dateRange }) => {
  const { t } = useTranslation()
  const { isDarkTheme } = useContext(DarkThemeContext)
  const { period, setPeriod } = periodState
  const { currentDate, setCurrentDate, start, setStart, end, setEnd } =
    dateRange

  const PERIOD_INCREMENT = {
    [PERIOD.HEBDO]: { weeks: 1 },
    [PERIOD.MENSUELLE]: { months: 1 },
    [PERIOD.SEMESTRIELLE]: { months: 1 },
    [PERIOD.ANNUELLE]: { months: 1 },
  }

  const handleChange = (next: boolean) => {
    const increment = PERIOD_INCREMENT[period]
    setCurrentDate(
      next ? add(currentDate, increment) : sub(currentDate, increment)
    )
  }
  useEffect(() => {
    const [startDate, endDate] = getPeriodDates(period, currentDate)
    setStart(startDate)
    setEnd(endDate)
  }, [period, currentDate])

  return (
    <div className="period-selector">
      <div className="period-selector-dropdown">
        <span className="text-[16px] font-medium">
          {t('prompts.kpi.period.label')}
        </span>
        <select
          className="dropdown-period form-input h-[30px]"
          value={period}
          onChange={(e) => setPeriod(e.target.value as string)}
        >
          <option value={PERIOD.HEBDO}>{t('prompts.kpi.period.hebdo')}</option>
          <option value={PERIOD.MENSUELLE}>
            {t('prompts.kpi.period.mensuelle')}
          </option>
          <option value={PERIOD.SEMESTRIELLE}>
            {t('prompts.kpi.period.semestrielle')}
          </option>
          <option value={PERIOD.ANNUELLE}>
            {t('prompts.kpi.period.annuelle')}
          </option>
        </select>
      </div>
      <div className="controls">
        <button
          onClick={() => handleChange(false)}
          className="button-selector group"
        >
          {isDarkTheme ? (
            <PrevIconDark className="button-selector-icon text-gray-1600 opacity-100 group-hover:opacity-0" />
          ) : (
            <PrevIcon className="button-selector-icon text-white opacity-100 group-hover:opacity-0" />
          )}
          <PrevIconHover className="button-selector-icon opacity-0 group-hover:opacity-100 text-blue-1050 dark:text-gray-1450" />
        </button>
        <span>
          {period === PERIOD.HEBDO ? (
            t('prompts.kpi.period.textHebdo') +
            format(start, 'dd/MM/yyyy', { locale: fr })
          ) : period === PERIOD.MENSUELLE ? (
            <span className="capitalize">
              {format(currentDate, 'MMMM yyyy', { locale: fr })}
            </span>
          ) : (
            <Trans
              i18nKey="prompts.kpi.period.textMois"
              values={{
                date1: format(start, 'MMMM yyyy', { locale: fr }),
                date2: format(end, 'MMMM yyyy', { locale: fr }),
              }}
            />
          )}
        </span>
        <button
          onClick={() => handleChange(true)}
          className="button-selector group"
        >
          {isDarkTheme ? (
            <NextIconDark className="button-selector-icon text-gray-1600 opacity-100 group-hover:opacity-0" />
          ) : (
            <NextIcon className="button-selector-icon opacity-100 group-hover:opacity-0 text-white" />
          )}

          <NextIconHover className="button-selector-icon opacity-0 group-hover:opacity-100 text-blue-1050 dark:text-gray-1450" />
        </button>
      </div>
    </div>
  )
}
